import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { Toaster as SonnerToaster } from 'sonner';

// Import the separated page components
import LandingPage from './LandingPage';
import EmployeeDashboard from './EmployeeDashboard';
import SuperAdminDashboard from './SuperAdminDashboard';
import AdminApp from './admin/AdminApp';
import SetPassword from './SetPassword';
import OnboardingForm from './OnboardingForm';
import { backendApi } from './api/backendApi';

// Roles that get the admin console. Everyone else gets the employee
// portal. There is no overlap and no switching between the two: these are
// two applications, and a session belongs to exactly one of them. HR is
// here because the console is where leave is approved, payroll is run and
// the organization is configured.
const ADMIN_ROLES = ['admin', 'hr'];

export default function HRMSApp() {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState('landing');
  // No synchronous guess anymore, there's nothing in localStorage to check.
  // Whether the user is logged in now depends entirely on the httpOnly
  // refresh-token cookie, which we can only find out about by asking the
  // server (see the effect below). `authChecked` gates rendering so this
  // never causes a login-page flash.
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // True once we've actually confirmed login state with the server.
  const [authChecked, setAuthChecked] = useState(false);
  const [transitionClass, setTransitionClass] = useState('page-enter-done');
  const [authBusy, setAuthBusy] = useState(false);

  // LOGIN STATE
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // SIGNUP STATE
  const [signupUsername, setSignupUsername] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // True once "Create account" has been submitted and is now waiting on
  // admin approval (mirrors the Request Access flow's "request sent" state).
  const [signupRequestSent, setSignupRequestSent] = useState(false);

  // REQUEST-ACCESS STATE (invite-based onboarding)
  const [requestName, setRequestName] = useState('');
  const [requestEmail, setRequestEmail] = useState('');

  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');

  const showAuthAlert = (message) => {
    toast.error(message, {
      duration: 4000,
      position: 'top-right',
    });
  };


  // Restore an existing HRMS session on mount. There's no token to read
  // from storage anymore, this asks the server to exchange the httpOnly
  // "remember me" refresh cookie (if the browser has one) for a fresh
  // access token. A refresh that comes back empty just means: not
  // remembered, or the remembered session expired/was revoked, either
  // way, straight to the landing page, no error shown.
  useEffect(() => {
    let active = true;
    (async () => {
      const user = await backendApi.auth.restoreSession();
      if (!active) return;
      if (user) {
        setIsLoggedIn(true);
        setUserName(user.full_name || user.name || '');
        setUserRole(user.role || '');
      } else {
        setIsLoggedIn(false);
      }
      setAuthChecked(true);
    })();
    return () => {
      active = false;
    };
  }, []);

  // Navigation with transition
  const navigatePage = (newPage) => {
    if (newPage === currentPage) return;

    setTransitionClass('page-exit');

    const timeoutId = setTimeout(() => {
      setCurrentPage(newPage);
      setTransitionClass('page-enter');
      setTimeout(() => setTransitionClass('page-enter-done'), 100);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  // LOGIN HANDLER, authenticates against HRMS_Backend. Returns true/false
  // so the caller (LandingPage) only persists "remember me" state, and only
  // triggers the browser's save-password prompt, after a CONFIRMED
  // successful login, never optimistically beforehand.
  const handleLoginSubmit = async () => {
    if (!loginEmail || !loginPassword) {
      showAuthAlert('Login failed: Email and password are required.');
      return false;
    }
    if (authBusy) return false;

    setAuthBusy(true);
    try {
      const user = await backendApi.auth.login(loginEmail, loginPassword,rememberMe);
      setIsLoggedIn(true);
      setUserName(user.full_name || user.name || loginEmail);
      setUserRole(user.role || '');
      return true;
    } catch (err) {
      showAuthAlert(
        err.status === 401
          ? 'Login failed: Invalid email or password.'
          : `Login failed: ${err.message || 'Unable to reach the server.'}`
      );
      return false;
    } finally {
      setAuthBusy(false);
    }
  };

  // SIGNUP HANDLER, submits an access request to HRMS_Backend for admin
  // approval. This intentionally does NOT create a live account or log the
  // person in: "Create account" and "Request access" are now the same
  // underlying flow, so every new user goes through admin approval before
  // they can sign in (no user ever ends up active/unapproved).
  const handleSignupSubmit = async () => {
    if (!signupUsername || !signupEmail) {
      showAuthAlert('Sign up failed: Full name and email are required.');
      return;
    }
    if (authBusy) return;

    setAuthBusy(true);
    try {
      await backendApi.auth.requestAccess({
        full_name: signupUsername,
        email: signupEmail,
      });
      setSignupRequestSent(true);

      setSignupUsername('');
      setSignupEmail('');
      setSignupPassword('');
      setConfirmPassword('');
    } catch (err) {
      // Any failure, including "couldn't match your email to a company" for
      // a wrong/personal domain, is just surfaced as-is. No dropdown, no
      // company names revealed; the user re-enters their correct company
      // email and resubmits.
      showAuthAlert(`Sign up failed: ${err.message || 'Unable to reach the server.'}`);
    } finally {
      setAuthBusy(false);
    }
  };

  // REQUEST ACCESS HANDLER, invite-based onboarding
  const handleRequestAccess = async () => {
    if (!requestName || !requestEmail) {
      showAuthAlert('Please enter your name and email.');
      return false;
    }
    if (authBusy) return false;

    setAuthBusy(true);
    try {
      await backendApi.auth.requestAccess({
        full_name: requestName,
        email: requestEmail,
      });
      setRequestName('');
      setRequestEmail('');
      return true;
    } catch (err) {
      // Any failure, including "couldn't match your email to a company" for
      // a wrong/personal domain, is just surfaced as-is. No dropdown, no
      // company names revealed; the user re-enters their correct company
      // email and resubmits.
      showAuthAlert(`Request failed: ${err.message || 'Unable to reach the server.'}`);
      return false;
    } finally {
      setAuthBusy(false);
    }
  };

const handleLogout = async () => {
  await backendApi.auth.logout();
  // NOTE: "Remember me" (email + password re-fill on the sign-in form) is
  // intentionally left alone on logout, logging out only ends the
  // session (the httpOnly refresh cookie is revoked server-side above).
  // LandingPage's mount effect re-populates the form from localStorage
  // for the fields the user asked to have remembered.
  setIsLoggedIn(false);
  setUserName('');
  setUserRole('');
  setLoginEmail('');
  setLoginPassword('');
  setSignupUsername('');
  setSignupEmail('');
  setSignupPassword('');
  setConfirmPassword('');
  navigatePage('landing');
};

  // RENDER
  const renderAppContent = () => {
    const pathname = location.pathname.replace(/\/+$/, '');

    // Handle the onboarding invite link: /hrms/onboarding/:token
    if (pathname.match(/\/onboarding\/[a-zA-Z0-9\-_]+$/)) {
      return <OnboardingForm />;
    }

    // Handle the emailed invite link: /hrms/set-password?token=...
    if (pathname.endsWith('/set-password')) {
      return <SetPassword />;
    }

    // Don't decide landing-vs-dashboard until we've actually confirmed login
    // state with the server, this is what prevents the login-page flash.
    if (!authChecked) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (isLoggedIn) {
      // Platform owner gets the cross-tenant console.
      if (userRole === 'superadmin') {
        return <SuperAdminDashboard onLogout={handleLogout} userName={userName} />;
      }

      // One role, one application. Organization administrators (admin, hr)
      // get the admin console; everybody else gets the employee portal.
      if (ADMIN_ROLES.includes(userRole)) {
        return <AdminApp onLogout={handleLogout} userName={userName} />;
      }

      return <EmployeeDashboard onLogout={handleLogout} userName={userName} />;

    } else if (currentPage === 'landing') {
      return (
        <LandingPage
          navigatePage={navigatePage}

          // LOGIN PROPS
          loginEmail={loginEmail}
          setLoginEmail={setLoginEmail}
          loginPassword={loginPassword}
          setLoginPassword={setLoginPassword}
          handleLoginSubmit={handleLoginSubmit}
          rememberMe={rememberMe}
          setRememberMe={setRememberMe}

          // SIGNUP PROPS
          signupUsername={signupUsername}
          setSignupUsername={setSignupUsername}
          signupEmail={signupEmail}
          setSignupEmail={setSignupEmail}
          signupPassword={signupPassword}
          setSignupPassword={setSignupPassword}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          handleSignupSubmit={handleSignupSubmit}
          signupRequestSent={signupRequestSent}
          setSignupRequestSent={setSignupRequestSent}

          // REQUEST-ACCESS PROPS
          requestName={requestName}
          setRequestName={setRequestName}
          requestEmail={requestEmail}
          setRequestEmail={setRequestEmail}
          handleRequestAccess={handleRequestAccess}
        />
      );
    }
  };

  return (
    <div className="relative overflow-hidden min-h-screen">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            fontSize: '14px',
            borderRadius: '8px',
          },
        }}
      />
      <SonnerToaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          style: { fontSize: '14px', borderRadius: '8px' },
        }}
      />
      <style>{`
        @keyframes slide-in-from-right {
          from { transform: translateX(100%); opacity: 0.5; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slide-out-to-left {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(-100%); opacity: 0; }
        }

        .page-transition-container {
          position: relative;
          width: 100vw;
          min-height: 100vh;
        }

        .page-exit {
          animation: slide-out-to-left 0.5s ease-in-out forwards;
        }

        .page-enter {
          animation: slide-in-from-right 0.5s ease-in-out forwards;
        }

        .page-enter-done {
          transform: translateX(0);
          opacity: 1;
        }
      `}</style>

      <div
        key={isLoggedIn ? 'dashboard' : currentPage}
        className={`page-transition-container absolute top-0 left-0 h-full w-full ${transitionClass}`}
      >
        {renderAppContent()}
      </div>
    </div>
  );
}